/**
 * Pagina gestione gruppi del tenant.
 *
 * CRUD gruppi con gestione membri (aggiungi/rimuovi utenti).
 * Visibile solo ad admin e super_admin.
 * Endpoint: GET/POST /api/v1/gruppi, PATCH/DELETE /api/v1/gruppi/{id},
 *           POST /api/v1/gruppi/{id}/membri, DELETE /api/v1/gruppi/{id}/membri/{uid}
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';

interface Membro {
  id: string;
  utente_id: string;
  nome: string;
  cognome: string;
  email: string;
}

interface GruppoData {
  id: string;
  nome: string;
  descrizione: string | null;
  parent_id: string | null;
  membri: Membro[];
  created_at: string;
}

interface UtenteOption {
  id: string;
  nome: string;
  cognome: string;
  email: string;
}

interface PermessoItem {
  codice: string;
  modulo: string;
  azione: string;
  descrizione: string | null;
}

const GruppiList: React.FC = () => {
  const token = localStorage.getItem('token') ?? '';

  const [gruppi, setGruppi] = useState<GruppoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  /* Dettaglio gruppo selezionato */
  const [selected, setSelected] = useState<GruppoData | null>(null);

  /* Form nuovo gruppo */
  const [showForm, setShowForm] = useState(false);
  const [formNome, setFormNome] = useState('');
  const [formDescrizione, setFormDescrizione] = useState('');
  const [formParent, setFormParent] = useState('__none__');
  const [saving, setSaving] = useState(false);

  /* Aggiungi membro */
  const [utenti, setUtenti] = useState<UtenteOption[]>([]);
  const [membroSelect, setMembroSelect] = useState('__none__');

  /* Permessi del gruppo selezionato */
  const [allPermessi, setAllPermessi] = useState<PermessoItem[]>([]);
  const [gruppoPermessi, setGruppoPermessi] = useState<Set<string>>(new Set());
  const [savingPermessi, setSavingPermessi] = useState(false);

  const caricaGruppi = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/gruppi', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      setGruppi(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const caricaUtenti = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/utenti?limit=200', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      setUtenti(await res.json());
    } catch {
      /* best-effort */
    }
  }, [token]);

  const caricaPermessi = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/admin/permessi', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setAllPermessi(await res.json());
    } catch { /* best-effort */ }
  }, [token]);

  const caricaPermessiGruppo = useCallback(async (gruppoId: string) => {
    try {
      const res = await fetch(`/api/v1/admin/permessi/gruppo/${gruppoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setGruppoPermessi(new Set(data.permessi));
      }
    } catch { /* best-effort */ }
  }, [token]);

  useEffect(() => {
    void caricaGruppi();
    void caricaUtenti();
    void caricaPermessi();
  }, [caricaGruppi, caricaUtenti, caricaPermessi]);

  useEffect(() => {
    if (selected) void caricaPermessiGruppo(selected.id);
    else setGruppoPermessi(new Set());
  }, [selected, caricaPermessiGruppo]);

  const creaGruppo = async () => {
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, string | null> = {
        nome: formNome,
        descrizione: formDescrizione || null,
        parent_id: formParent === '__none__' ? null : formParent,
      };
      const res = await fetch('/api/v1/gruppi', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      setShowForm(false);
      setFormNome('');
      setFormDescrizione('');
      setFormParent('__none__');
      void caricaGruppi();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSaving(false);
    }
  };

  const eliminaGruppo = async (gruppoId: string) => {
    if (!confirm('Eliminare questo gruppo?')) return;
    setError(null);
    try {
      const res = await fetch(`/api/v1/gruppi/${gruppoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      setSelected(null);
      void caricaGruppi();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    }
  };

  const aggiungiMembro = async () => {
    if (!selected || membroSelect === '__none__') return;
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/gruppi/${selected.id}/membri`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ utente_id: membroSelect }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      const updated: GruppoData = await res.json();
      setSelected(updated);
      setMembroSelect('__none__');
      setSuccess('Membro aggiunto');
      void caricaGruppi();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    }
  };

  const rimuoviMembro = async (utenteId: string) => {
    if (!selected) return;
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/gruppi/${selected.id}/membri/${utenteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      /* Ricarica il gruppo selezionato */
      const resGruppo = await fetch(`/api/v1/gruppi/${selected.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resGruppo.ok) setSelected(await resGruppo.json());
      setSuccess('Membro rimosso');
      void caricaGruppi();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    }
  };

  const togglePermesso = (codice: string) => {
    setGruppoPermessi((prev) => {
      const next = new Set(prev);
      if (next.has(codice)) next.delete(codice); else next.add(codice);
      return next;
    });
  };

  const salvaPermessiGruppo = async () => {
    if (!selected) return;
    setSavingPermessi(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/admin/permessi/gruppo/${selected.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ permessi: [...gruppoPermessi] }),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      setSuccess(`Permessi gruppo "${selected.nome}" salvati`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSavingPermessi(false);
    }
  };

  /** Utenti non ancora membri del gruppo selezionato. */
  const utentiDisponibili = selected
    ? utenti.filter((u) => !selected.membri.some((m) => m.utente_id === u.id))
    : [];

  const parentName = (parentId: string | null): string => {
    if (!parentId) return '—';
    const p = gruppi.find((g) => g.id === parentId);
    return p ? p.nome : parentId.slice(0, 8);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Gruppi
        </h1>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Annulla' : 'Nuovo gruppo'}
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
      )}
      {success && (
        <div className="rounded-md bg-green-500/10 p-3 text-sm text-green-700 dark:text-green-400">
          {success}
        </div>
      )}

      {/* Form nuovo gruppo */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Nuovo gruppo</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Input
              placeholder="Nome gruppo"
              value={formNome}
              onChange={(e) => setFormNome(e.target.value)}
            />
            <Input
              placeholder="Descrizione (opzionale)"
              value={formDescrizione}
              onChange={(e) => setFormDescrizione(e.target.value)}
            />
            <Select value={formParent} onValueChange={setFormParent}>
              <SelectTrigger>
                <SelectValue placeholder="Gruppo padre" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Nessuno (root)</SelectItem>
                {gruppi.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-end md:col-span-3">
              <Button onClick={creaGruppo} disabled={saving || !formNome.trim()}>
                {saving ? 'Salvataggio…' : 'Crea gruppo'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Lista gruppi */}
        <Card>
          <CardHeader>
            <CardTitle>Gruppi ({gruppi.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading && (
              <p className="p-6 text-center text-sm text-muted-foreground">Caricamento…</p>
            )}
            {!loading && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Padre</TableHead>
                    <TableHead>Membri</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gruppi.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                        Nessun gruppo
                      </TableCell>
                    </TableRow>
                  )}
                  {gruppi.map((g) => (
                    <TableRow
                      key={g.id}
                      className={`cursor-pointer ${selected?.id === g.id ? 'bg-accent/10' : ''}`}
                      onClick={() => setSelected(g)}
                    >
                      <TableCell className="font-medium">{g.nome}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {parentName(g.parent_id)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{g.membri.length}</Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            void eliminaGruppo(g.id);
                          }}
                          className="text-destructive hover:text-destructive"
                        >
                          Elimina
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Dettaglio gruppo selezionato */}
        {selected ? (
          <Card>
            <CardHeader>
              <CardTitle>{selected.nome}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selected.descrizione && (
                <p className="text-sm text-muted-foreground">{selected.descrizione}</p>
              )}

              <div>
                <h3 className="mb-2 text-sm font-medium">
                  Membri ({selected.membri.length})
                </h3>
                {selected.membri.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nessun membro</p>
                ) : (
                  <div className="space-y-2">
                    {selected.membri.map((m) => (
                      <div
                        key={m.utente_id}
                        className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                      >
                        <span>
                          {m.cognome} {m.nome}{' '}
                          <span className="text-muted-foreground">({m.email})</span>
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void rimuoviMembro(m.utente_id)}
                          className="text-destructive hover:text-destructive"
                        >
                          Rimuovi
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Aggiungi membro */}
              {utentiDisponibili.length > 0 && (
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="mb-1 block text-xs text-muted-foreground">
                      Aggiungi membro
                    </label>
                    <Select value={membroSelect} onValueChange={setMembroSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona utente" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Seleziona…</SelectItem>
                        {utentiDisponibili.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.cognome} {u.nome} ({u.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={() => void aggiungiMembro()}
                    disabled={membroSelect === '__none__'}
                  >
                    Aggiungi
                  </Button>
                </div>
              )}

              {/* Permessi del gruppo */}
              {allPermessi.length > 0 && (
                <div className="border-t pt-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-medium">Permessi del gruppo</h3>
                    <Button
                      size="sm"
                      onClick={() => void salvaPermessiGruppo()}
                      disabled={savingPermessi}
                    >
                      {savingPermessi ? '…' : 'Salva permessi'}
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {(() => {
                      const moduli = [...new Set(allPermessi.map((p) => p.modulo))];
                      const moduloLabels: Record<string, string> = {
                        core: 'Sistema', documentale: 'Documentale', fornitori: 'Fornitori',
                        magazzino: 'Magazzino', cassa: 'Cassa', ordini: 'Ordini', prezzi: 'Prezzi',
                      };
                      return moduli.map((modulo) => (
                        <div key={modulo}>
                          <p className="mb-1 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            {moduloLabels[modulo] ?? modulo}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {allPermessi.filter((p) => p.modulo === modulo).map((perm) => {
                              const active = gruppoPermessi.has(perm.codice);
                              return (
                                <button
                                  key={perm.codice}
                                  onClick={() => togglePermesso(perm.codice)}
                                  className="rounded-md border px-2.5 py-1 text-xs transition-colors"
                                  style={{
                                    backgroundColor: active ? 'var(--color-primary)' : 'transparent',
                                    color: active ? 'white' : 'var(--color-muted-foreground)',
                                    borderColor: active ? 'var(--color-primary)' : 'var(--color-border)',
                                  }}
                                  title={perm.descrizione ?? ''}
                                >
                                  {perm.descrizione ?? perm.azione}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="flex h-48 items-center justify-center">
              <p className="text-sm text-muted-foreground">
                Seleziona un gruppo per vedere i dettagli
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default GruppiList;
